import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';
import { useTheme } from '../../contexts/ThemeContext';

export interface TimelineEvent {
  id: string;
  start: string;
  end?: string;
  label: string;
  status?: 'success' | 'failure' | 'in_progress' | 'neutral';
  metadata?: Record<string, any>;
}

export interface TimelineLane {
  id: string;
  label: string;
  color: string;
  events: TimelineEvent[];
}

interface SwimLaneTimelineProps {
  lanes: TimelineLane[];
  dateRange?: { from: string; to: string };
  onEventClick?: (event: TimelineEvent) => void;
  correlations?: { fromEventId: string; toEventId: string }[];
  showDensityIndicator?: boolean;
  height?: number;
}

const STATUS_COLORS: Record<string, { fill: string; glow: string }> = {
  success: { fill: '#22c55e', glow: 'rgba(34,197,94,0.3)' },
  failure: { fill: '#ef4444', glow: 'rgba(239,68,68,0.3)' },
  in_progress: { fill: '#f59e0b', glow: 'rgba(245,158,11,0.4)' },
  neutral: { fill: '#6b7280', glow: 'rgba(107,114,128,0.2)' },
};

const SwimLaneTimeline: React.FC<SwimLaneTimelineProps> = ({
  lanes,
  dateRange,
  onEventClick,
  correlations,
  height: laneHeight = 48,
}) => {
  const { darkMode } = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; event: TimelineEvent } | null>(null);

  useEffect(() => {
    if (!containerRef.current || lanes.length === 0) return;
    const el = containerRef.current;
    d3.select(el).selectAll('svg').remove();

    const margin = { top: 30, right: 20, bottom: 20, left: 100 };
    const width = el.clientWidth;
    const chartHeight = lanes.length * laneHeight;
    const totalHeight = chartHeight + margin.top + margin.bottom;

    const allEvents = lanes.flatMap(l => l.events);
    let tMin: Date, tMax: Date;
    if (dateRange) {
      tMin = new Date(dateRange.from);
      tMax = new Date(dateRange.to);
    } else {
      const starts = allEvents.map(e => new Date(e.start));
      const ends = allEvents.filter(e => e.end).map(e => new Date(e.end!));
      const allTimes = [...starts, ...ends];
      tMin = d3.min(allTimes) || new Date();
      tMax = d3.max(allTimes) || new Date();
      const pad = Math.max((tMax.getTime() - tMin.getTime()) * 0.05, 3600000);
      tMin = new Date(tMin.getTime() - pad);
      tMax = new Date(tMax.getTime() + pad);
    }

    const xScale = d3.scaleTime().domain([tMin, tMax]).range([margin.left, width - margin.right]);
    const yScale = d3.scaleBand().domain(lanes.map(l => l.id)).range([margin.top, margin.top + chartHeight]).padding(0.2);

    const svg = d3.select(el).append('svg')
      .attr('width', width)
      .attr('height', totalHeight);

    svg.append('defs').append('clipPath').attr('id', 'tl-clip')
      .append('rect').attr('x', margin.left).attr('y', margin.top)
      .attr('width', width - margin.left - margin.right).attr('height', chartHeight);

    const chartGroup = svg.append('g').attr('clip-path', 'url(#tl-clip)');
    const gridColor = darkMode ? '#2d3748' : '#e5e7eb';

    // Grid lines
    svg.append('g')
      .attr('transform', `translate(0,${margin.top})`)
      .call(d3.axisTop(xScale).ticks(8).tickSize(-chartHeight).tickFormat(() => '' as any))
      .call(g => g.select('.domain').remove())
      .call(g => g.selectAll('.tick line').attr('stroke', gridColor).attr('stroke-dasharray', '2,2'));

    // Time axis
    svg.append('g')
      .attr('transform', `translate(0,${margin.top})`)
      .call(d3.axisTop(xScale).ticks(8).tickFormat(d3.timeFormat('%b %d') as any))
      .call(g => g.select('.domain').attr('stroke', gridColor))
      .call(g => g.selectAll('text').attr('fill', darkMode ? '#9ca3af' : '#6b7280').attr('font-size', 10));

    // Lane labels + separators
    lanes.forEach((lane, i) => {
      const y = yScale(lane.id)! + yScale.bandwidth() / 2;
      svg.append('text')
        .attr('x', margin.left - 8).attr('y', y)
        .attr('text-anchor', 'end').attr('dominant-baseline', 'middle')
        .attr('font-size', 11).attr('fill', lane.color).attr('font-weight', 500)
        .text(lane.label);

      if (i > 0) {
        const sepY = margin.top + i * laneHeight;
        svg.append('line')
          .attr('x1', margin.left).attr('x2', width - margin.right)
          .attr('y1', sepY).attr('y2', sepY)
          .attr('stroke', gridColor).attr('stroke-width', 0.5);
      }
    });

    // Render events
    lanes.forEach(lane => {
      const laneY = yScale(lane.id)!;
      const barH = yScale.bandwidth();

      lane.events.forEach(event => {
        const x1 = xScale(new Date(event.start));
        const x2 = event.end ? xScale(new Date(event.end)) : x1 + 6;
        const isPoint = !event.end || x2 - x1 < 4;
        const colors = STATUS_COLORS[event.status || 'neutral'];

        if (isPoint) {
          chartGroup.append('circle')
            .attr('cx', x1).attr('cy', laneY + barH / 2).attr('r', 4)
            .attr('fill', colors.fill)
            .attr('stroke', darkMode ? '#1a1a2e' : '#fff').attr('stroke-width', 1.5)
            .attr('cursor', 'pointer')
            .on('click', () => onEventClick?.(event))
            .on('mouseenter', function(e: any) {
              d3.select(this).attr('r', 6);
              setTooltip({ x: e.clientX, y: e.clientY, event });
            })
            .on('mouseleave', function() {
              d3.select(this).attr('r', 4);
              setTooltip(null);
            });
        } else {
          const barW = Math.max(x2 - x1, 4);
          chartGroup.append('rect')
            .attr('x', x1).attr('y', laneY + 4)
            .attr('width', barW).attr('height', barH - 8)
            .attr('rx', 4).attr('fill', colors.fill).attr('opacity', 0.85)
            .attr('cursor', 'pointer')
            .on('click', () => onEventClick?.(event))
            .on('mouseenter', function(e: any) {
              d3.select(this).attr('opacity', 1).attr('filter', `drop-shadow(0 0 4px ${colors.glow})`);
              setTooltip({ x: e.clientX, y: e.clientY, event });
            })
            .on('mouseleave', function() {
              d3.select(this).attr('opacity', 0.85).attr('filter', null as any);
              setTooltip(null);
            });

          if (barW > 60) {
            chartGroup.append('text')
              .attr('x', x1 + 6).attr('y', laneY + barH / 2)
              .attr('dominant-baseline', 'middle').attr('font-size', 9)
              .attr('fill', '#fff').attr('pointer-events', 'none')
              .text(event.label.length > barW / 6 ? event.label.slice(0, Math.floor(barW / 6)) + '...' : event.label);
          }
        }
      });
    });

    // Correlation lines
    if (correlations && correlations.length > 0) {
      const eventMap = new Map<string, { x: number; y: number }>();
      lanes.forEach(lane => {
        const laneY = yScale(lane.id)! + yScale.bandwidth() / 2;
        lane.events.forEach(event => {
          const x = event.end
            ? (xScale(new Date(event.start)) + xScale(new Date(event.end))) / 2
            : xScale(new Date(event.start));
          eventMap.set(event.id, { x, y: laneY });
        });
      });

      correlations.forEach(({ fromEventId, toEventId }) => {
        const from = eventMap.get(fromEventId);
        const to = eventMap.get(toEventId);
        if (from && to) {
          chartGroup.append('line')
            .attr('x1', from.x).attr('y1', from.y)
            .attr('x2', to.x).attr('y2', to.y)
            .attr('stroke', darkMode ? '#4a5568' : '#cbd5e0')
            .attr('stroke-width', 1).attr('stroke-dasharray', '4,3').attr('opacity', 0.6);
        }
      });
    }

  }, [lanes, dateRange, correlations, darkMode, laneHeight, onEventClick]);

  return (
    <div className="relative">
      <div ref={containerRef} className="w-full overflow-hidden" />
      {tooltip && (
        <div
          className="fixed z-50 px-3 py-2 rounded-lg shadow-lg text-xs max-w-xs pointer-events-none"
          style={{
            left: tooltip.x + 12,
            top: tooltip.y - 40,
            backgroundColor: darkMode ? '#1f2937' : '#fff',
            border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
            color: darkMode ? '#e5e7eb' : '#1f2937',
          }}
        >
          <p className="font-semibold">{tooltip.event.label}</p>
          <p className="opacity-70">{new Date(tooltip.event.start).toLocaleString()}</p>
          {tooltip.event.end && <p className="opacity-70">to {new Date(tooltip.event.end).toLocaleString()}</p>}
          {tooltip.event.status && <p className="opacity-70 capitalize">Status: {tooltip.event.status}</p>}
        </div>
      )}
    </div>
  );
};

export default SwimLaneTimeline;
